import { ListingModel, ItemModel } from 'models';
import { ItemType, RawItemData } from 'models/Item/types';
import { ListingType } from 'models/Listing/types';
import { RawMetricData, MetricModel } from 'models/Market/Metric';

export function calculateMetricsForListings(listings: ListingModel[], key: number): MetricModel {
	const totalListings = listings.length;

	if (totalListings === 0) {
		return {
			key: key,
			mean: 0,
			open: 0,
			close: 0,
			max: 0,
			min: 0,
			growth: 0,
			totalListings: 0,
			totalSales: 0,
			variance: 0
		};
	}

	let sum = 0;
	let max = Number.MIN_SAFE_INTEGER;
	let min = Number.MAX_SAFE_INTEGER;

	for (const listing of listings) {
		sum += listing.rawPrice;
		if (listing.rawPrice > max) max = listing.rawPrice;
		if (listing.rawPrice < min) min = listing.rawPrice;
	}

	const mean = sum / totalListings;
	let varianceSum = 0;
	for (const listing of listings) {
		varianceSum += Math.pow(listing.rawPrice - mean, 2);
	}
	const variance = varianceSum / totalListings;
	const open = listings[0].rawPrice;
	const close = listings[totalListings - 1].rawPrice;
	const growth = ((close - open) / open) * 100;
	const totalSales = sum;

	const rawMetricData: RawMetricData = {
		close,
		growth,
		max,
		mean,
		min,
		open,
		total_listings: totalListings,
		total_sales: totalSales,
		variance,
		key: key
	};

	return new MetricModel(rawMetricData);
}

export function getListingsByDateCombo(
 listings: ListingModel[],
 year: number,
 month?: number,
 day?: number
): ListingModel[] {
	return listings.filter(listing => {
		if (listing.date) {
			if (day) {
				return (
					listing.date.year() === year &&
					listing.date.month() + 1 === month &&
					listing.date.date() === day
				);
			} else if (month) {
				return (
					listing.date.year() === year &&
					listing.date.month() + 1 === month
				);
			} else {
				return listing.date.year() === year;
			}
		}
		return false;
	});
};

export function createPreviewItemFromListing(listing: ListingModel): ItemModel {
  const mappedType: ItemType = (() => {
    switch (listing.type) {
      case ListingType.PRINT:
        return ItemType.PRINT;
      case ListingType.SCULPTURE:
        return ItemType.SCULPTURE;
      case ListingType.MISC:
        return ItemType.MISC;
      default:
        return ItemType.UNKNOWN;
    }
  })();

  const rawItem: RawItemData = {
    id: '',
    date: '',
    description: '',
    edition: listing.edition || '',
    image: listing.image,
    source_link: listing.sourceLink || '',
    name: listing.name,
    release_price: '',
    publisher: '',
    measurements: listing.measurements || '',
    dimensions: listing.dimensions || {},
    material: listing.material || '',
    type: mappedType,
    artist: listing.artist,
    artist_id: listing.artistId,
    listings: [listing.id],
    market: '',
    release_info: {
      symbol: '',
      raw_price: '',
      date: '',
      converted_price: 0,
    },
    edition_info: {
      size: listing.editionInfo.size || 0,
      variants: [],
      is_open: false,
    },
    authenticity_info: {
      certificate_issued: false,
      dated: false,
      has_fakes: false,
      numbered: false,
      signed: false,
    },
    hand_treated: false,
    market_summary: {
      close: listing.rawPrice,
      growth: 0,
      max: listing.rawPrice,
      mean: listing.rawPrice,
      min: listing.rawPrice,
      open: listing.rawPrice,
      total_listings: 1,
      total_sales: 1,
      variance: 0,
      estimated_value: listing.rawPrice,
      secondary_market_premium: 0,
      last_sale_percentage_change: 0,
      last_sale_date: listing.date?.toISOString() || '',
      first_sale_date: listing.date?.toISOString() || '',
    },
    material_ids: listing.materialIds || [],
    colors: [],
    active_market: { bids: [], asks: [] }
  };

  return new ItemModel(rawItem);
}
